import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import PhoneWithCountry, { isPhoneValid } from "@/components/forms/PhoneWithCountry";
import { trackConversion, pushDL } from "@/lib/analytics";

const LoanForm = forwardRef(function LoanForm(_, ref) {
  const { t, isRTL, lh } = useLang();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    loan_amount_aed: 1000000,
    loan_tenure_years: 25,
    monthly_income_aed: 30000,
    employment_type: "Salaried",
    full_name: "",
    phone: "",
  });
  const [agreed, setAgreed] = useState(false);

  useImperativeHandle(ref, () => ({
    // Accepts an optional init payload from the Mortgage Calculator apply
    // button (e.g. { loan_amount_aed, loan_tenure_years }) so the funnel
    // hands off with the exact numbers the visitor just modelled.
    open: (init) => {
      setStep(1);
      setDone(false);
      setAgreed(false);
      if (init && typeof init === "object") {
        setForm((f) => ({ ...f, ...init }));
      }
      setOpen(true);
    },
  }));

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  const next = () => setStep((s) => Math.min(3, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!form.full_name || !form.full_name.trim()) { toast.error(isRTL ? "أدخل اسمك الكامل." : "Please enter your full name."); return; }
    if (!isPhoneValid(form.phone)) { toast.error(isRTL ? "أدخل رقم هاتف صحيح (8 أرقام على الأقل)." : "Please enter a valid phone (min 8 digits)."); return; }
    if (!agreed) { toast.error(t("consultation.agree_error")); return; }
    setSending(true);
    try {
      await api.post("/leads", {
        ...form,
        loan_amount_aed: Number(form.loan_amount_aed),
        loan_tenure_years: Number(form.loan_tenure_years),
        monthly_income_aed: Number(form.monthly_income_aed),
        source: "loan",
      });
      trackConversion("loan", { value: Number(form.loan_amount_aed) });
      pushDL("form_submit", {
        form_name: "loan",
        form_id: "loan-form",
        loan_amount_aed: Number(form.loan_amount_aed),
      });
      pushDL("generate_lead", {
        form_name: "loan",
        value: Number(form.loan_amount_aed),
        currency: "AED",
      });
      setDone(true);
      toast.success(t("loan.success"));
    } catch {
      toast.error(isRTL ? "حدث خطأ. يُرجى المحاولة مرة أخرى." : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const employmentOptions = t("loan.fields.employment_types");
  // R2-FIN-05: stable English ids are stored/sent; only the visible label is localised.
  const EMPLOYMENT_IDS = ["Salaried", "Self-employed", "Business Owner"];
  const ArrowNext = isRTL ? ArrowLeft : ArrowRight;
  const ArrowPrev = isRTL ? ArrowRight : ArrowLeft;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white text-propertic-navy max-w-lg rounded-2xl border hairline" data-testid="loan-modal">
        <DialogHeader>
          <DialogTitle className="font-editorial text-3xl text-propertic-navy">{t("loan.title")}</DialogTitle>
          <DialogDescription className="text-propertic-muted">{t("loan.subtitle")}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-10 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-propertic-cream grid place-items-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-propertic-gold" />
            </div>
            <p className="font-editorial text-xl text-propertic-navy">{t("loan.success")}</p>
          </div>
        ) : (
          <div className="mt-2" data-testid="loan-form">
            <div className="flex items-center gap-2 mb-6">
              {[1, 2, 3].map((s) => (
                <div key={s} className={`h-1 flex-1 rounded-full ${step >= s ? "bg-propertic-gold" : "bg-propertic-hairline"}`} />
              ))}
            </div>
            <p className="font-mono-brand text-propertic-gold mb-4">{t("loan.step")} {step} {t("loan.of")} 3</p>

            {step === 1 && (
              <div className="space-y-5">
                <TextField label={t("loan.fields.loan_amount")} testid="lf-loan-amount" type="number" value={form.loan_amount_aed} onChange={update("loan_amount_aed")} />
                <TextField label={t("loan.fields.tenure")} testid="lf-tenure" type="number" value={form.loan_tenure_years} onChange={update("loan_tenure_years")} />
              </div>
            )}
            {step === 2 && (
              <div className="space-y-5">
                <TextField label={t("loan.fields.income")} testid="lf-income" type="number" value={form.monthly_income_aed} onChange={update("monthly_income_aed")} />
                <label className="block">
                  <span className="font-mono-brand text-propertic-muted">{t("loan.fields.employment")}</span>
                  <Select value={form.employment_type} onValueChange={update("employment_type")}>
                    <SelectTrigger data-testid="lf-employment" className="mt-2 bg-white rounded-xl border hairline text-propertic-navy focus:ring-0 focus:border-propertic-gold h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border hairline">
                      {employmentOptions.map((opt, i) => (
                        <SelectItem key={EMPLOYMENT_IDS[i]} value={EMPLOYMENT_IDS[i]}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5">
                <TextField label={`${t("loan.fields.full_name")} *`} testid="lf-name" value={form.full_name} onChange={update("full_name")} required />
                <PhoneWithCountry label={t("loan.fields.phone")} testid="lf-phone" value={form.phone} onChange={update("phone")} required />
                <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    data-testid="lf-agree"
                    className="mt-0.5 w-4 h-4 shrink-0 accent-propertic-gold cursor-pointer"
                  />
                  <span className="text-[12px] text-propertic-muted leading-[1.5]">
                    {t("consultation.agree_prefix")}{" "}
                    <Link to={lh("/terms")} target="_blank" className="text-propertic-gold underline hover:text-propertic-gold-2">{t("consultation.agree_terms")}</Link>
                    {" "}{t("consultation.agree_and")}{" "}
                    <Link to={lh("/privacy")} target="_blank" className="text-propertic-gold underline hover:text-propertic-gold-2">{t("consultation.agree_privacy")}</Link>.
                  </span>
                </label>
              </div>
            )}

            <div className="flex items-center justify-between mt-8 gap-3">
              <button
                type="button"
                onClick={prev}
                disabled={step === 1}
                data-testid="lf-back"
                className="rounded-full h-11 px-5 border hairline text-xs font-mono-brand text-propertic-navy hover:bg-propertic-cream transition-colors disabled:opacity-40 inline-flex items-center gap-2"
              >
                <ArrowPrev className="w-3.5 h-3.5" /> {t("loan.back")}
              </button>
              {step < 3 ? (
                <button
                  type="button"
                  onClick={next}
                  data-testid="lf-next"
                  className="btn-gold rounded-full h-11 px-6 text-xs font-mono-brand inline-flex items-center gap-2"
                >
                  {t("loan.next")} <ArrowNext className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={sending}
                  data-testid="lf-submit"
                  className="btn-gold rounded-full h-11 px-6 text-xs font-mono-brand disabled:opacity-60"
                >
                  {sending ? "…" : t("loan.submit")}
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
});

function TextField({ label, testid, ...rest }) {
  return (
    <label className="block">
      <span className="font-mono-brand text-propertic-muted">{label}</span>
      <input
        {...rest}
        data-testid={testid}
        className="mt-2 w-full bg-white rounded-xl border hairline px-4 h-11 text-propertic-navy text-sm focus:border-propertic-gold outline-none transition-colors"
      />
    </label>
  );
}

export default LoanForm;
